import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ARCADE_LEVELS } from './arcadeData';

const BEAT_KEY = (id) => `${id}_beat`;

// Star positions are fixed to avoid re-generating on every render
const STARS = Array.from({ length: 50 }, (_, i) => ({
  left: ((i * 137.5) % 100).toFixed(2),
  top: ((i * 97.3) % 100).toFixed(2),
  size: (((i * 7) % 3) + 1).toFixed(1),
  opacity: (((i * 11) % 6) / 10 + 0.2).toFixed(2),
}));

const LockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="11" width="14" height="11" rx="2" fill="#3a3a4a" stroke="#555" strokeWidth="1.5" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="#555" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CheckBadge = () => (
  <div style={{
    position: 'absolute',
    right: -6,
    top: -6,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#4caf50',
    border: '2px solid #050508',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    color: '#fff',
    fontWeight: 'bold',
  }}>
    ✓
  </div>
);

const CIRCLE_ICONS = ['👨‍⚕️', '🛵', '🥧', '🚄', '🥁', '⌚'];

const ArcadeLevelSelect = () => {
  const navigate = useNavigate();

  const unlockedStatus = useMemo(() =>
    ARCADE_LEVELS.map((level, i) => {
      if (i === 0) return true;
      return localStorage.getItem(BEAT_KEY(ARCADE_LEVELS[i - 1].id)) === 'true';
    }),
    []
  );

  const beatStatus = useMemo(() =>
    ARCADE_LEVELS.map(level => localStorage.getItem(BEAT_KEY(level.id)) === 'true'),
    []
  );

  // Highest unlocked index = the "active" circle that blinks
  const activeLevelIndex = unlockedStatus.reduce((last, unlocked, i) => unlocked ? i : last, 0);

  // Display top-to-bottom: Grand Timekeeper … Dr. Tempus
  const displayOrder = ARCADE_LEVELS.map((level, origIndex) => ({ level, origIndex })).reverse();

  const handleCircleClick = (level, origIndex) => {
    if (!unlockedStatus[origIndex]) return;
    localStorage.setItem('arcadeReturn', '/arcade');
    navigate(`/arcade/${level.id}`);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 50% 40%, #0d0d1f 0%, #050508 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Courier New', monospace",
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Star field */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {STARS.map((s, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: s.size + 'px',
            height: s.size + 'px',
            background: '#fff',
            borderRadius: '50%',
            left: s.left + '%',
            top: s.top + '%',
            opacity: s.opacity,
          }} />
        ))}
      </div>

      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        style={{
          position: 'absolute', top: 20, left: 20,
          background: 'transparent',
          border: '1px solid #333',
          color: '#666',
          padding: '7px 14px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          letterSpacing: '2px',
          fontSize: '0.7rem',
          transition: 'color 0.2s, border-color 0.2s',
        }}
        onMouseEnter={e => { e.target.style.color = '#aaa'; e.target.style.borderColor = '#666'; }}
        onMouseLeave={e => { e.target.style.color = '#666'; e.target.style.borderColor = '#333'; }}
      >
        ← BACK
      </button>

      {/* Title */}
      <div style={{
        color: '#ffd700',
        fontSize: '1.8rem',
        fontWeight: 'bold',
        letterSpacing: '6px',
        marginBottom: '6px',
        textShadow: '0 0 30px rgba(255,215,0,0.5)',
      }}>
        ARCADE MODE
      </div>
      <div style={{ color: '#444', fontSize: '0.65rem', letterSpacing: '4px', marginBottom: '48px' }}>
        CHRONOLOGICAL SANCTUM
      </div>

      {/* Level tower */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {displayOrder.map(({ level, origIndex }, displayIdx) => {
          const unlocked = unlockedStatus[origIndex];
          const beaten = beatStatus[origIndex];
          const isActive = origIndex === activeLevelIndex;
          const isLast = displayIdx === displayOrder.length - 1;

          // Line below this circle (connecting to the next/lower one)
          const nextItem = !isLast ? displayOrder[displayIdx + 1] : null;
          const nextUnlocked = nextItem ? unlockedStatus[nextItem.origIndex] : false;

          return (
            <div key={level.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

              {/* Circle row */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>

                {/* The circle */}
                <div
                  onClick={() => handleCircleClick(level, origIndex)}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: unlocked
                      ? `radial-gradient(circle at 35% 30%, ${level.difficultyColor}55 0%, ${level.difficultyColor}1a 70%, transparent 100%)`
                      : '#0c0c18',
                    border: `2px solid ${unlocked ? level.difficultyColor : '#252530'}`,
                    cursor: unlocked ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.4rem',
                    position: 'relative',
                    zIndex: 2,
                    boxShadow: isActive
                      ? `0 0 0 3px ${level.difficultyColor}44, 0 0 30px ${level.difficultyColor}66`
                      : unlocked
                        ? `0 0 12px ${level.difficultyColor}33`
                        : 'none',
                    animation: isActive ? 'circleGlow 1.4s ease-in-out infinite' : 'none',
                    transition: 'box-shadow 0.3s',
                  }}
                  onMouseEnter={e => {
                    if (unlocked) e.currentTarget.style.transform = 'scale(1.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {!unlocked ? <LockIcon /> : CIRCLE_ICONS[origIndex]}
                  {beaten && <CheckBadge />}
                </div>

                {/* Label (only for unlocked levels) */}
                {unlocked && (
                  <div style={{
                    position: 'absolute',
                    left: 80,
                    whiteSpace: 'nowrap',
                  }}>
                    <div style={{
                      color: isActive ? '#fff' : '#999',
                      fontSize: '0.82rem',
                      fontWeight: isActive ? '700' : '400',
                      letterSpacing: '1px',
                      textShadow: isActive ? `0 0 10px ${level.difficultyColor}` : 'none',
                    }}>
                      {level.characterName}
                    </div>
                    <div style={{
                      color: level.difficultyColor,
                      fontSize: '0.6rem',
                      letterSpacing: '3px',
                      opacity: isActive ? 1 : 0.7,
                    }}>
                      {level.difficulty}
                    </div>
                  </div>
                )}
              </div>

              {/* Connecting line segment to next circle */}
              {!isLast && (
                <div style={{
                  width: 2,
                  height: 52,
                  background: nextUnlocked
                    ? `linear-gradient(to bottom, ${level.difficultyColor}88, ${displayOrder[displayIdx + 1].level.difficultyColor}55)`
                    : '#1a1a28',
                  zIndex: 1,
                  transition: 'background 0.5s',
                }} />
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes circleGlow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.75; transform: scale(1.06); }
        }
      `}</style>
    </div>
  );
};

export default ArcadeLevelSelect;
